import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Spin,
  Alert,
  Segmented,
} from 'antd';
import useBreakpoint from '../../../hooks/useBreakpoint';
import PartnerRemoteSelect from '../../investors/components/PartnerRemoteSelect';
import offlineProductSalesService from '../../../services/offlineProductSalesService';
import {
  COMMISSION_STATUS_OPTIONS,
  IFA_COMMISSION_MODE_AMOUNT,
  IFA_COMMISSION_MODE_OPTIONS,
  IFA_COMMISSION_MODE_PERCENT,
  ISIN_LENGTH,
  OFFLINE_IFA_PARTNER_LIST_PARAMS,
  PRODUCT_TYPE_OTHER,
} from './offlineProductSales.constants';
import {
  apiErrMsg,
  buildCreatePayload,
  buildUpdatePayload,
  convertIfaCommissionBetweenModes,
  formatCurrency,
  isIfaExceedsGross,
  normalizeIsin,
  normalizePan,
  parseIfaCommissionInput,
  recordToFormValues,
  resolveRevenueMode,
  validateBuyerPan,
  validateCreateFields,
  validateIsin,
  validateRevenueFields,
} from './offlineProductSales.helpers';

const { TextArea } = Input;

export default function OfflineProductSalesFormDrawer({
  open,
  editingUuid,
  productTypes,
  rmOptions,
  rmUnavailable,
  partnerUnavailable,
  onClose,
  onSuccess,
}) {
  const [form] = Form.useForm();
  const isAboveSm = useBreakpoint('sm');
  const isEdit = Boolean(editingUuid);

  const [loadingRecord, setLoadingRecord] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [initialRecord, setInitialRecord] = useState(null);
  const [revenueError, setRevenueError] = useState(null);

  const calculateTimerRef = useRef(null);

  const grossRevenue = Form.useWatch('gross_revenue', form);
  const ifaCommissionInput = Form.useWatch('ifa_commission', form);
  const ifaCommissionMode =
    Form.useWatch('ifa_commission_mode', form) || IFA_COMMISSION_MODE_PERCENT;
  const productType = Form.useWatch('product_type', form);

  const parsedIfa = useMemo(
    () => parseIfaCommissionInput(ifaCommissionInput, grossRevenue, ifaCommissionMode),
    [ifaCommissionInput, grossRevenue, ifaCommissionMode],
  );
  const resolvedIfaCommission = parsedIfa.amount;

  const revenueMode = resolveRevenueMode(
    grossRevenue,
    resolvedIfaCommission,
    ifaCommissionMode,
  );
  const isAutoMode = revenueMode === 'auto';
  const ifaExceedsGross = isAutoMode && isIfaExceedsGross(grossRevenue, resolvedIfaCommission);
  const ifaInputError = parsedIfa.error;

  const handleIfaModeChange = (nextMode) => {
    const converted = convertIfaCommissionBetweenModes({
      value: ifaCommissionInput,
      fromMode: ifaCommissionMode,
      toMode: nextMode,
      grossRevenue,
    });
    form.setFieldsValue({
      ifa_commission_mode: nextMode,
      ifa_commission: converted.value,
    });
    if (converted.error) {
      setRevenueError(converted.error);
    } else {
      setRevenueError(null);
    }
  };

  const ifaHelpText = (() => {
    if (ifaInputError) return ifaInputError;
    if (
      ifaCommissionMode === IFA_COMMISSION_MODE_PERCENT &&
      resolvedIfaCommission != null &&
      grossRevenue != null &&
      parsedIfa.percent != null
    ) {
      return (
        <span className="text-gray-500">
          {parsedIfa.percent}% of {formatCurrency(grossRevenue)} ={' '}
          {formatCurrency(resolvedIfaCommission)}
        </span>
      );
    }
    if (
      ifaCommissionMode === IFA_COMMISSION_MODE_AMOUNT &&
      resolvedIfaCommission != null &&
      grossRevenue != null &&
      parsedIfa.percent != null
    ) {
      return (
        <span className="text-gray-500">
          {formatCurrency(resolvedIfaCommission)} = {parsedIfa.percent}% of{' '}
          {formatCurrency(grossRevenue)}
        </span>
      );
    }
    if (ifaCommissionMode === IFA_COMMISSION_MODE_AMOUNT) {
      return <span className="text-gray-500">Enter absolute amount (e.g. 3000)</span>;
    }
    return <span className="text-gray-500">Enter % of gross (e.g. 1 for 1%)</span>;
  })();

  const clearCalculateTimer = () => {
    if (calculateTimerRef.current) {
      clearTimeout(calculateTimerRef.current);
      calculateTimerRef.current = null;
    }
  };

  const runCalculate = useCallback(async (gross, ifa) => {
    if (gross == null || ifa == null || isIfaExceedsGross(gross, ifa)) {
      return;
    }
    setCalculating(true);
    try {
      const result = await offlineProductSalesService.calculate({
        gross_revenue: Number(gross),
        ifa_commission: Number(ifa),
      });
      if (result?.altcase_revenue != null) {
        form.setFieldValue('altcase_revenue', result.altcase_revenue);
      }
    } catch {
      // Preview failure is non-blocking; submit still validates server-side
    } finally {
      setCalculating(false);
    }
  }, [form]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setInitialRecord(null);
      setRevenueError(null);
      clearCalculateTimer();
      return undefined;
    }

    if (!editingUuid) {
      form.resetFields();
      setInitialRecord(null);
      setRevenueError(null);
      return undefined;
    }

    let cancelled = false;
    setLoadingRecord(true);
    offlineProductSalesService
      .getByUuid(editingUuid)
      .then((record) => {
        if (cancelled) return;
        setInitialRecord(record);
        form.setFieldsValue(recordToFormValues(record));
      })
      .catch((err) => {
        if (!cancelled) {
          setRevenueError(apiErrMsg(err));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingRecord(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, editingUuid, form]);

  useEffect(() => {
    if (!open || !isAutoMode) {
      clearCalculateTimer();
      if (!isAutoMode) setRevenueError(null);
      return undefined;
    }

    if (ifaExceedsGross) {
      clearCalculateTimer();
      setRevenueError('IFA commission cannot exceed gross revenue');
      return undefined;
    }

    setRevenueError(null);
    clearCalculateTimer();
    calculateTimerRef.current = setTimeout(() => {
      runCalculate(grossRevenue, resolvedIfaCommission);
    }, 300);

    return clearCalculateTimer;
  }, [open, isAutoMode, grossRevenue, resolvedIfaCommission, ifaExceedsGross, runCalculate]);

  const handleClose = () => {
    form.resetFields();
    setInitialRecord(null);
    setRevenueError(null);
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (values.product_type === PRODUCT_TYPE_OTHER) {
        const customName = (values.custom_product_name || '').trim();
        if (customName.length < 2 || customName.length > 128) {
          form.setFields([
            {
              name: 'custom_product_name',
              errors: ['Custom product name must be between 2 and 128 characters'],
            },
          ]);
          return;
        }
      }

      const createFieldsError = validateCreateFields(values, { isEdit });
      if (createFieldsError) {
        setRevenueError(createFieldsError);
        return;
      }

      const revenueValidationError = validateRevenueFields(values);
      if (revenueValidationError) {
        setRevenueError(revenueValidationError);
        return;
      }

      setSubmitting(true);
      setRevenueError(null);

      if (isEdit) {
        const payload = buildUpdatePayload(values, initialRecord);
        if (Object.keys(payload).length === 0) {
          handleClose();
          return;
        }
        await offlineProductSalesService.update(editingUuid, payload);
      } else {
        const payload = buildCreatePayload(values);
        await offlineProductSalesService.create(payload);
      }

      onSuccess();
      handleClose();
    } catch (err) {
      if (err?.errorFields) return;
      const msg = apiErrMsg(err);
      if (/isin/i.test(msg)) {
        form.setFields([{ name: 'isin', errors: [msg] }]);
        return;
      }
      setRevenueError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const productTypeOptions = (productTypes || []).map((type) => ({
    value: type,
    label: type,
  }));

  const panRules = [
    ...(isEdit ? [] : [{ required: true, message: 'Buyer PAN is required' }]),
    {
      validator: (_, value) => {
        const err = validateBuyerPan(value, { required: !isEdit });
        return err ? Promise.reject(new Error(err)) : Promise.resolve();
      },
    },
  ];

  const isinRules = [
    {
      validator: (_, value) => {
        const err = validateIsin(value);
        return err ? Promise.reject(new Error(err)) : Promise.resolve();
      },
    },
  ];

  const ifaPartnerRules = isEdit
    ? []
    : [{ required: true, message: 'IFA Name is required' }];

  return (
    <Drawer
      title={isEdit ? 'Edit offline sale' : 'Add offline sale'}
      open={open}
      onClose={handleClose}
      width={isAboveSm ? 520 : '100%'}
      destroyOnClose
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={submitting}
            disabled={loadingRecord || ifaExceedsGross || Boolean(ifaInputError)}
          >
            {isEdit ? 'Save changes' : 'Create'}
          </Button>
        </div>
      }
    >
      {loadingRecord ? (
        <div className="flex justify-center py-12">
          <Spin size="large" />
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
          initialValues={{ ifa_commission_mode: IFA_COMMISSION_MODE_PERCENT }}
        >
          {revenueError && (
            <Alert type="error" message={revenueError} showIcon className="mb-4" />
          )}

          <Form.Item name="ifa_commission_mode" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="product_type"
            label="Product type"
            rules={[{ required: true, message: 'Product type is required' }]}
          >
            <Select placeholder="Select product type" options={productTypeOptions} showSearch optionFilterProp="label" />
          </Form.Item>

          {productType === PRODUCT_TYPE_OTHER && (
            <Form.Item
              name="custom_product_name"
              label="Custom product name"
              rules={[
                { required: true, message: 'Custom product name is required' },
                { min: 2, message: 'Must be at least 2 characters' },
                { max: 128, message: 'Must be at most 128 characters' },
              ]}
            >
              <Input placeholder="e.g. Structured Debt" maxLength={128} />
            </Form.Item>
          )}

          <Form.Item
            name="buyer_name"
            label="Buyer name"
            rules={[
              { required: true, message: 'Buyer name is required' },
              { max: 255, message: 'Must be at most 255 characters' },
            ]}
          >
            <Input placeholder="Buyer name" maxLength={255} />
          </Form.Item>

          <Form.Item name="buyer_pan" label="Buyer PAN" rules={panRules}>
            <Input
              placeholder="e.g. ABCDE1234F"
              maxLength={10}
              onChange={(e) => form.setFieldValue('buyer_pan', normalizePan(e.target.value))}
            />
          </Form.Item>

          <Form.Item name="isin" label="ISIN" rules={isinRules}>
            <Input
              placeholder="e.g. INE123456789"
              maxLength={ISIN_LENGTH}
              onChange={(e) => form.setFieldValue('isin', normalizeIsin(e.target.value))}
            />
          </Form.Item>

          <Form.Item name="security_name" label="Security name" rules={[{ max: 512, message: 'Must be at most 512 characters' }]}>
            <Input placeholder="Optional security / bond label" maxLength={512} />
          </Form.Item>

          <Form.Item
            name="deal_date"
            label="Deal date"
            rules={[{ required: true, message: 'Deal date is required' }]}
          >
            <DatePicker className="w-full" format="DD MMM YYYY" />
          </Form.Item>

          <Form.Item
            name="rm_uuid"
            label="RM"
            rules={[{ required: true, message: 'RM is required' }]}
          >
            <Select
              placeholder={rmUnavailable ? 'RM list unavailable' : 'Select RM'}
              options={rmOptions}
              showSearch
              optionFilterProp="label"
              disabled={rmUnavailable}
            />
          </Form.Item>

          <Form.Item name="gross_revenue" label="Gross revenue">
            <InputNumber className="w-full" min={0} precision={2} placeholder="Optional" />
          </Form.Item>

          <Form.Item
            label="IFA commission"
            validateStatus={ifaInputError ? 'error' : undefined}
            help={ifaHelpText}
          >
            <div className="flex gap-2 items-center">
              <Segmented
                size="small"
                options={IFA_COMMISSION_MODE_OPTIONS}
                value={ifaCommissionMode}
                onChange={handleIfaModeChange}
              />
              <Form.Item name="ifa_commission" noStyle>
                <Input
                  className="flex-1"
                  placeholder={
                    ifaCommissionMode === IFA_COMMISSION_MODE_AMOUNT
                      ? 'e.g. 3000'
                      : 'e.g. 1 or 2.5'
                  }
                  inputMode="decimal"
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[₹,%]/g, '');
                    form.setFieldValue('ifa_commission', raw);
                  }}
                />
              </Form.Item>
            </div>
          </Form.Item>

          <Form.Item name="ifa_partner_uuid" label="IFA Name" rules={ifaPartnerRules}>
            <PartnerRemoteSelect
              className="w-full"
              listParams={OFFLINE_IFA_PARTNER_LIST_PARAMS}
              pageSize={20}
              searchDebounceMs={300}
              disabled={partnerUnavailable}
              placeholder={partnerUnavailable ? 'Partner list unavailable' : 'Search and select IFA partner'}
              prefetchOnMount={Boolean(isEdit && initialRecord?.ifa?.uuid)}
              initialPartner={initialRecord?.ifa ?? null}
            />
          </Form.Item>

          <Form.Item
            name="altcase_revenue"
            label="Altcase revenue"
            rules={
              isAutoMode
                ? []
                : [{ required: true, message: 'Altcase revenue is required in manual mode' }]
            }
            extra={
              isAutoMode ? (
                <span className="text-gray-500">
                  {calculating ? 'Calculating…' : 'Auto-calculated from Gross − IFA'}
                </span>
              ) : (
                <span className="text-gray-500">Enter manually</span>
              )
            }
          >
            <InputNumber
              className="w-full"
              min={0}
              precision={2}
              disabled={isAutoMode}
              placeholder={isAutoMode ? 'Auto-calculated' : 'Enter altcase revenue'}
            />
          </Form.Item>

          <Form.Item name="commission_paid_status" label="Commission paid status">
            <Select placeholder="Select status" allowClear options={COMMISSION_STATUS_OPTIONS} />
          </Form.Item>

          <Form.Item name="paid_date" label="Paid date">
            <DatePicker className="w-full" format="DD MMM YYYY" />
          </Form.Item>

          <Form.Item name="paid_date_note" label="Paid date note" rules={[{ max: 255, message: 'Must be at most 255 characters' }]}>
            <Input placeholder='Use when no paid date (e.g. "informed amt to Rakesh")' maxLength={255} />
          </Form.Item>

          <Form.Item name="remarks" label="Remarks" rules={[{ max: 2000, message: 'Must be at most 2000 characters' }]}>
            <TextArea rows={3} placeholder="Optional remarks" maxLength={2000} showCount />
          </Form.Item>
        </Form>
      )}
    </Drawer>
  );
}
